import { t } from "@lingui/macro";
import { useSubmitProgressMutation } from "@src/__generated__/graphql";
import { NumberInput } from "@src/components/ui-kit";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { percent } from "@src/utils/formatters";
import { fieldNumberModelToInputProps } from "@src/utils/forms/inputHelpers";
import { maxValue, minValue } from "@src/utils/forms/validators";
import { useStore as useAppStore } from "@src/utils/hooks";
import { NumberModel } from "@src/utils/models";
import { FieldState } from "formstate";
import { debounce } from "lodash";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo } from "react";
import type { ProjectWip, SubRow } from "../../models";
import { useStore } from "../../Store";

const T_DEBOUNCE = 1 * 1000; // 1sec

interface SubmitProgressProps {
  projectWip: ProjectWip;
  subRow: SubRow;
  justify: "start" | "center" | "end";
}

const SubmitProgress = ({
  projectWip,
  subRow,
  justify,
}: SubmitProgressProps) => {
  const store = useStore();
  const appStore = useAppStore();

  const field = useMemo(
    () =>
      new FieldState(new NumberModel(+subRow.progress_percent, 2)).validators(
        minValue(0),
        maxValue(100),
      ),
    [subRow.progress_percent],
  );

  const [submit] = useSubmitProgressMutation({
    variables: {
      input: {
        id: subRow.work_in_progress_id,
        progress: field.$.denominatedValue,
      },
    },
    onCompleted(data) {
      appStore.UIStore.toast({ title: t`Saved`, status: "success" });

      store.refetchSummary();

      if (data.submitProgress) {
        const {
          changedWorkInProgressItemStats,
          lastWorkInProgressProjectStat,
        } = data.submitProgress;

        changedWorkInProgressItemStats.forEach((i) => {
          if (i.work_in_progress_id === subRow.work_in_progress_id) {
            subRow.setProgressPercent(i.progress_percent);
            subRow.setProgressChange(i.progress_change);
            subRow.setProgressChangePercent(i.progress_change_percent);
          }
        });

        projectWip.setProgressPercent(
          lastWorkInProgressProjectStat.progress_percent,
        );
        projectWip.setProgressChange(
          lastWorkInProgressProjectStat.progress_change,
        );
        projectWip.setProgressChangePercent(
          lastWorkInProgressProjectStat.progress_change_percent,
        );
      }
    },
    ...onError(),
  });

  const debouncedSubmit = useMemo(() => debounce(submit, T_DEBOUNCE), []);

  useEffect(() => debouncedSubmit.cancel, []);

  const onChangeExtra = async () => {
    const { hasError } = await field.validate();

    if (!hasError) {
      debouncedSubmit();
    }
  };

  if (
    subRow.is_closed ||
    !can(["wip_submit_all", "wip_submit_team", "wip_submit_own"])
  ) {
    return (
      <React.Fragment>{percent.format(subRow.progress_percent)}</React.Fragment>
    );
  }

  return (
    <div className={`flex justify-${justify}`}>
      <div style={{ width: "7.5rem" }}>
        <NumberInput
          suffix="%"
          minValue={0}
          maxValue={100}
          disabled={subRow.deleted}
          {...fieldNumberModelToInputProps(field, onChangeExtra)}
        />
      </div>
    </div>
  );
};

export default observer(SubmitProgress);
